// src/services/cosmetics.ts
import {
  collection,
  doc,
  getDoc,
  onSnapshot,
  type Unsubscribe,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';

import { db, functions } from '../lib/firebase';
import type { CosmeticSlot, UserCosmeticLoadout } from '../../types';
import { cosmeticCatalog, getCosmeticById } from '../constants/cosmetics';

export type OwnedCosmeticRecord = {
  id: string;
  cosmeticId: string;
  slot: CosmeticSlot;
  name: string;
  rarity: string;
  grantSource: 'starter' | 'purchase' | 'tournament' | 'admin' | string;
  createdAt?: any;
  updatedAt?: any;
  [key: string]: any;
};

type EnsureStarterCosmeticsResult = {
  ok: boolean;
  uid: string;
  grantedIds?: string[];
  existingIds?: string[];
  loadoutSeededSlots?: string[];
};

function ownedCosmeticRef(userId: string, cosmeticId: string) {
  return doc(db, 'users', userId, 'ownedCosmetics', cosmeticId);
}

function userRef(userId: string) {
  return doc(db, 'users', userId);
}

export function subscribeToOwnedCosmetics(
  userId: string,
  onChange: (items: Record<string, OwnedCosmeticRecord>) => void,
  onError?: (err: unknown) => void,
): Unsubscribe {
  const uid = String(userId || '').trim();
  if (!uid) {
    onChange({});
    return () => undefined;
  }

  return onSnapshot(
    collection(db, 'users', uid, 'ownedCosmetics'),
    (snap) => {
      const next: Record<string, OwnedCosmeticRecord> = {};
      snap.forEach((d) => {
        const data = d.data() as any;
        const cosmeticId = String(data?.cosmeticId || d.id);
        next[cosmeticId] = {
          id: d.id,
          cosmeticId,
          slot: data?.slot,
          name: data?.name,
          rarity: data?.rarity,
          grantSource: data?.grantSource || 'unknown',
          ...data,
        } as OwnedCosmeticRecord;
      });
      onChange(next);
    },
    (err) => {
      if (onError) onError(err);
      else console.error('[cosmetics] subscribeToOwnedCosmetics error', err);
      onChange({});
    },
  );
}

/**
 * Starter ownership is server-authoritative.
 * Keeping the userId argument preserves existing call sites; the callable always
 * grants to request.auth.uid and never trusts a client-supplied target uid.
 */
export async function ensureStarterCosmetics(userId: string): Promise<void> {
  const uid = String(userId || '').trim();
  if (!uid) return;

  const callEnsureStarterCosmetics = httpsCallable<Record<string, never>, EnsureStarterCosmeticsResult>(
    functions,
    'ensureStarterCosmetics',
  );

  const result = await callEnsureStarterCosmetics({});
  const returnedUid = String(result.data?.uid || '').trim();

  if (returnedUid && returnedUid !== uid) {
    throw new Error('Starter cosmetic bootstrap returned a different authenticated user.');
  }
}

/**
 * Compatibility alias. The server grant now seeds missing starter ownership and
 * any empty default loadout slots in one idempotent transaction.
 */
export async function seedDefaultCosmeticLoadout(userId: string): Promise<void> {
  await ensureStarterCosmetics(userId);
}

export type PurchaseAvatarCosmeticResult = {
  ok: boolean;
  cosmeticId: string;
  shellBalance?: number;
  shells?: number;
  alreadyOwned?: boolean;
};

export async function purchaseAvatarCosmetic(
  cosmeticId: string,
): Promise<PurchaseAvatarCosmeticResult> {
  const id = String(cosmeticId || '').trim();
  if (!id) throw new Error('purchaseAvatarCosmetic requires a cosmetic id');

  const callPurchaseCosmetic = httpsCallable<
    { cosmeticId: string },
    PurchaseAvatarCosmeticResult
  >(functions, 'purchaseCosmetic');

  const result = await callPurchaseCosmetic({ cosmeticId: id });
  return result.data;
}

export async function equipAvatarCosmetic(
  userId: string,
  cosmeticId: string | null,
): Promise<UserCosmeticLoadout> {
  const uid = String(userId || '').trim();
  if (!uid) throw new Error('equipAvatarCosmetic requires a user id');
  if (!cosmeticId) throw new Error('equipAvatarCosmetic requires a cosmetic id');

  const item = getCosmeticById(cosmeticId);
  if (!item) throw new Error(`Unknown cosmetic: ${cosmeticId}`);

  let ownedSnap = await getDoc(ownedCosmeticRef(uid, item.id));

  // Existing users may predate the locked ownership rules. Repair the fixed
  // starter set through the server, then re-check ownership before equipping.
  if (!ownedSnap.exists() && item.starter) {
    await ensureStarterCosmetics(uid);
    ownedSnap = await getDoc(ownedCosmeticRef(uid, item.id));
  }

  if (!ownedSnap.exists()) {
    throw new Error('You do not own this cosmetic yet.');
  }

  const callSetCosmeticLoadout = httpsCallable<
    { action: 'equip'; cosmeticId: string },
    {
      ok: boolean;
      uid: string;
      action: 'equip';
      slot: CosmeticSlot;
      cosmeticId: string;
      loadoutPatch?: Partial<UserCosmeticLoadout>;
      cosmeticLoadout?: UserCosmeticLoadout;
    }
  >(functions, 'setCosmeticLoadout');

  const result = await callSetCosmeticLoadout({
    action: 'equip',
    cosmeticId: item.id,
  });

  const returnedUid = String(result.data?.uid || '').trim();
  if (returnedUid && returnedUid !== uid) {
    throw new Error('Cosmetic equip returned a different authenticated user.');
  }

  return (
    result.data?.cosmeticLoadout ||
    result.data?.loadoutPatch || {
      [item.slot]: item.id,
    }
  ) as UserCosmeticLoadout;
}

export async function unequipAvatarCosmeticSlot(
  userId: string,
  slot: CosmeticSlot,
): Promise<UserCosmeticLoadout> {
  const uid = String(userId || '').trim();
  if (!uid) throw new Error('unequipAvatarCosmeticSlot requires a user id');

  const callSetCosmeticLoadout = httpsCallable<
    { action: 'unequip'; slot: CosmeticSlot },
    {
      ok: boolean;
      uid: string;
      action: 'unequip';
      slot: CosmeticSlot;
      cosmeticId: null;
      loadoutPatch?: Partial<UserCosmeticLoadout>;
      cosmeticLoadout?: UserCosmeticLoadout;
    }
  >(functions, 'setCosmeticLoadout');

  const result = await callSetCosmeticLoadout({ action: 'unequip', slot });
  const returnedUid = String(result.data?.uid || '').trim();
  if (returnedUid && returnedUid !== uid) {
    throw new Error('Cosmetic unequip returned a different authenticated user.');
  }

  return (
    result.data?.cosmeticLoadout ||
    result.data?.loadoutPatch || {
      [slot]: null,
    }
  ) as UserCosmeticLoadout;
}

export function getCosmeticCatalogForInventory() {
  return cosmeticCatalog.slice();
}
